if (!window.awRidgeHeroLoaded) {
  window.awRidgeHeroLoaded = true;

  const AW_PAUSE_VIDEO_ON_REDUCED_MOTION = false;
  const AW_UNLOCK_EVENTS = ['touchstart', 'pointerdown', 'keydown', 'scroll'];
  const AW_SWIPE_MIN_DISTANCE = 40;
  const AW_SWIPE_MAX_DURATION = 700;

  function onMediaChange(mq, handler) {
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    mq.addListener(handler);
    return () => mq.removeListener(handler);
  }

  class AwRidgeHero {
    constructor(section) {
      this.section = section;
      this.slides = Array.from(section.querySelectorAll('[data-aw-ridge-hero-slide]'));
      this.dots = Array.from(section.querySelectorAll('[data-aw-ridge-hero-dot]'));
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.interval = Number(section.dataset.interval) || 6000;
      this.autoplay = section.dataset.autoplay === 'true';

      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reduceMotion = this.motionQuery.matches;

      this.inView = true;
      this.timer = null;
      this.unlockBound = false;
      this.cleanups = [];

      this.onMouseEnter = () => this.stop();
      this.onMouseLeave = () => this.start();
      this.onVisibility = () => {
        if (document.hidden) this.stop();
        else this.start();
        this.syncMedia();
      };
      this.onMotionChange = () => {
        this.reduceMotion = this.motionQuery.matches;
        this.restart();
        this.syncMedia();
      };
      this.onUnlock = () => {
        this.unbindUnlock();
        this.syncMedia();
      };

      this.swipe = null;
      this.onPointerDown = (event) => {
        if (event.pointerType === 'mouse' || this.slides.length < 2) return;
        this.swipe = { x: event.clientX, y: event.clientY, at: Date.now() };
      };
      this.onPointerUp = (event) => {
        const start = this.swipe;
        this.swipe = null;
        if (!start || event.pointerType === 'mouse') return;

        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Date.now() - start.at > AW_SWIPE_MAX_DURATION) return;
        if (Math.abs(dx) < AW_SWIPE_MIN_DISTANCE || Math.abs(dx) <= Math.abs(dy)) return;

        this.show(this.index + (dx < 0 ? 1 : -1));
        this.restart();
      };
      this.onPointerCancel = () => {
        this.swipe = null;
      };
      this.onArrowKey = (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.show(this.index + (event.key === 'ArrowRight' ? 1 : -1));
        this.restart();
        const dot = this.dots[this.index];
        if (dot) dot.focus();
      };

      this.bind();
      this.observe();
      this.syncMedia();
      this.start();
    }

    bind() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.show(Number(dot.dataset.index));
          this.restart();
        });
      });

      this.section.addEventListener('mouseenter', this.onMouseEnter);
      this.section.addEventListener('mouseleave', this.onMouseLeave);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.cleanups.push(onMediaChange(this.motionQuery, this.onMotionChange));

      if (this.slides.length > 1) {
        this.section.addEventListener('pointerdown', this.onPointerDown, { passive: true });
        this.section.addEventListener('pointerup', this.onPointerUp, { passive: true });
        this.section.addEventListener('pointercancel', this.onPointerCancel, { passive: true });

        const dotGroup = this.section.querySelector('.aw-ridge-hero__dots');
        if (dotGroup) dotGroup.addEventListener('keydown', this.onArrowKey);
      }
    }

    observe() {
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (this.inView) this.start();
            else this.stop();
            this.syncMedia();
          });
        },
        { threshold: 0.01 }
      );
      this.observer.observe(this.section);
    }

    show(next) {
      if (!this.slides.length) return;
      const i = (next + this.slides.length) % this.slides.length;

      this.slides.forEach((slide, index) => {
        const active = index === i;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.dots.forEach((dot, index) => {
        const active = index === i;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-current', active ? 'true' : 'false');
      });

      this.index = i;
      this.syncMedia();
    }

    get videoAllowed() {
      if (!this.inView) return false;
      if (AW_PAUSE_VIDEO_ON_REDUCED_MOTION && this.reduceMotion) return false;
      return true;
    }

    playVideo(video) {
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.autoplay = true;
      if (video.preload === 'none') video.preload = 'auto';

      const promise = video.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch((error) => {
          if (window.Shopify && window.Shopify.designMode) {
            console.warn('[aw-ridge-hero] video.play() bị chặn:', error && error.name);
          }
          this.bindUnlock();
        });
      }
    }

    bindUnlock() {
      if (this.unlockBound) return;
      this.unlockBound = true;
      AW_UNLOCK_EVENTS.forEach((name) => {
        document.addEventListener(name, this.onUnlock, { passive: true });
      });
    }

    unbindUnlock() {
      if (!this.unlockBound) return;
      this.unlockBound = false;
      AW_UNLOCK_EVENTS.forEach((name) => {
        document.removeEventListener(name, this.onUnlock);
      });
    }

    syncMedia() {
      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('video');
        if (!video) return;

        if (index === this.index && this.videoAllowed) this.playVideo(video);
        else if (!video.paused) video.pause();
      });
    }

    start() {
      if (
        !this.autoplay ||
        this.reduceMotion ||
        this.slides.length < 2 ||
        document.hidden ||
        !this.inView ||
        this.timer
      )
        return;

      this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    restart() {
      this.stop();
      this.start();
    }

    selectBlock(blockId) {
      const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
      if (index >= 0) {
        this.show(index);
        this.stop();
      }
    }

    destroy() {
      this.stop();
      this.unbindUnlock();
      if (this.observer) this.observer.disconnect();
      this.section.removeEventListener('mouseenter', this.onMouseEnter);
      this.section.removeEventListener('mouseleave', this.onMouseLeave);
      this.section.removeEventListener('pointerdown', this.onPointerDown);
      this.section.removeEventListener('pointerup', this.onPointerUp);
      this.section.removeEventListener('pointercancel', this.onPointerCancel);
      document.removeEventListener('visibilitychange', this.onVisibility);
      this.cleanups.forEach((fn) => fn());
      this.cleanups = [];
    }
  }

  function init(container) {
    (container || document).querySelectorAll('[data-aw-ridge-hero]').forEach((section) => {
      if (section.awRidgeHero) section.awRidgeHero.destroy();
      section.awRidgeHero = new AwRidgeHero(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('[data-aw-ridge-hero]');
    if (section && section.awRidgeHero) section.awRidgeHero.destroy();
  });

  document.addEventListener('shopify:block:select', (event) => {
    const section = event.target.closest('[data-aw-ridge-hero]');
    if (section && section.awRidgeHero) section.awRidgeHero.selectBlock(event.detail.blockId);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    const section = event.target.closest('[data-aw-ridge-hero]');
    if (section && section.awRidgeHero) section.awRidgeHero.start();
  });
}